import { useRef, useState, useEffect, useMemo } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { useReactToPrint } from 'react-to-print'
import { Printer, ArrowLeft, Loader2, AlertCircle, FileText, CheckCircle2 } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { QuizScript } from '@quebon/shared-learning'
import {
  QuizRenderer,
  LAYOUT,
  STEP4_LAYOUT,
  PAPER_SIZES,
  QUIZZES_PER_STEP,
  BLEED_SIZE_MM,
  mmToPx,
  getPageLayout,
  toQuizData,
  PageHeader,
  StepHeader,
  PageFooter,
  ChapterNumber,
  TitleQueture,
  TitleStepSvg,
  PrintQuizItem,
  Step4Section,
  MemoPage,
  ModuleSummaryPage,
  TocPage,
  type TocModuleData,
  type TocLesson,
  useQuizMeasurement,
  usePageSplitter,
} from '@quebon/shared-learning'
import { quizScriptApi } from '@/api/quizScript'
import { printBookApi } from '@/api/printBook'

// Step별 퀴즈 수 (테스트용 — 추후 콘텐츠 기반으로 변경 예정)
const STEP_QUIZ_COUNTS = [5, 3, 5]
const TOTAL_QUIZ_NEEDED = STEP_QUIZ_COUNTS.reduce((a, b) => a + b, 0)

// Step4 하드코딩 질문 (추후 문제 ID로 대체 예정)
const STEP4_QUESTIONS = [
  '이 단원에서 배운 내용 중 가장 기억에 남는 것은 무엇인가요?',
]

function distributeQuizzes(quizzes: QuizScript[]): { steps: QuizScript[][]; flat: QuizScript[] } {
  if (quizzes.length === 0) return { steps: [[], [], []], flat: [] }
  const filled: QuizScript[] = []
  for (let i = 0; i < TOTAL_QUIZ_NEEDED; i++) {
    filled.push(quizzes[i % quizzes.length])
  }
  const steps: QuizScript[][] = []
  let offset = 0
  for (const count of STEP_QUIZ_COUNTS) {
    steps.push(filled.slice(offset, offset + count))
    offset += count
  }
  return { steps, flat: filled }
}

function padMeasuredHeights(measuredHeights: number[]): number[] {
  const result: number[] = []
  let srcIdx = 0
  for (const count of STEP_QUIZ_COUNTS) {
    for (let i = 0; i < count; i++) {
      result.push(measuredHeights[srcIdx++] || 400)
    }
    for (let i = count; i < QUIZZES_PER_STEP; i++) {
      result.push(0)
    }
  }
  return result
}

export function LessonPreviewPage() {
  const { bookId, lessonId } = useParams<{ bookId: string; lessonId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const printRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const [bleedEnabled, setBleedEnabled] = useState(false)

  const previewType = searchParams.get('type')

  // ─── 모듈 요약 프리뷰 ─────────────────────────────────────────────
  if (previewType === 'module-summary') {
    const moduleIndex = Number(searchParams.get('moduleIndex') || '0')
    const moduleName = searchParams.get('moduleName') || '모듈'
    const startPage = Number(searchParams.get('startPage') || '4')
    const lessonsJson = searchParams.get('lessons')
    const moduleLessons: { lessonName: string; pageCount: number | null }[] = lessonsJson
      ? JSON.parse(lessonsJson)
      : []

    const paper = PAPER_SIZES.A4
    const pageWidthPx = mmToPx(paper.width)
    const pageHeightPx = mmToPx(paper.height)
    const modBleedPx = bleedEnabled ? mmToPx(BLEED_SIZE_MM) : 0
    const modTotalW = pageWidthPx + modBleedPx * 2
    const modTotalWmm = paper.width + (bleedEnabled ? BLEED_SIZE_MM * 2 : 0)
    const modTotalHmm = paper.height + (bleedEnabled ? BLEED_SIZE_MM * 2 : 0)

    const modulePrintRef = useRef<HTMLDivElement>(null)
    const moduleReactToPrintFn = useReactToPrint({
      contentRef: modulePrintRef,
      documentTitle: `Module_${moduleIndex + 1}_${moduleName}`,
      pageStyle: `
        @page { size: ${modTotalWmm}mm ${modTotalHmm}mm; margin: 0; }
        @media print {
          html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print-page { page-break-after: always !important; break-after: page !important; }
          .print-page:last-child { page-break-after: auto !important; break-after: auto !important; }
        }
      `,
    })

    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-white sticky top-0 z-10 print:hidden">
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <FileText size={14} />
            <span>Module {moduleIndex + 1}: {moduleName} · p.{startPage}~{startPage + 1}</span>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-slate-600">
              도련 (3mm)
              <button
                type="button"
                role="switch"
                aria-checked={bleedEnabled}
                onClick={() => setBleedEnabled(!bleedEnabled)}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                  bleedEnabled ? 'bg-amber-500' : 'bg-gray-300'
                }`}
              >
                <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  bleedEnabled ? 'translate-x-4' : 'translate-x-0'
                }`} />
              </button>
            </label>
            <button
              onClick={() => navigate(`/contents/books/${bookId}?tab=3`)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              <ArrowLeft className="w-4 h-4" /> 돌아가기
            </button>
            <button
              onClick={() => moduleReactToPrintFn()}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-amber-500 text-slate-900 rounded-lg text-sm font-medium hover:bg-amber-400 transition-colors"
            >
              <Printer size={16} /> PDF 다운로드
            </button>
          </div>
        </div>
        <div className="flex overflow-auto flex-1 justify-center py-6">
          <div
            ref={modulePrintRef}
            className="bg-white border border-gray-300 shadow-lg print:shadow-none print:border-0"
            style={{ width: modTotalW }}
          >
            <ModuleSummaryPage
              moduleIndex={moduleIndex}
              moduleName={moduleName}
              lessons={moduleLessons}
              pageWidth={pageWidthPx}
              pageHeight={pageHeightPx}
              bleedPx={modBleedPx}
            />
          </div>
        </div>
      </div>
    )
  }

  // ─── 목차 프리뷰 ──────────────────────────────────────────────────
  if (previewType === 'toc') {
    const bookLabelToc = searchParams.get('bookLabel') || ''
    const modulesJson = searchParams.get('modules')
    const rawModules: { moduleName: string; chapterNumber?: number; lessons: { lessonName: string; startPage: number | null; grade?: string; unitInfo?: string }[] }[] =
      modulesJson ? JSON.parse(modulesJson) : []

    const paper = PAPER_SIZES.A4
    const pageWidthPx = mmToPx(paper.width)
    const pageHeightPx = mmToPx(paper.height)
    const tocBleedPx = bleedEnabled ? mmToPx(BLEED_SIZE_MM) : 0
    const tocTotalW = pageWidthPx + tocBleedPx * 2
    const tocTotalH = pageHeightPx + tocBleedPx * 2
    const tocTotalWmm = paper.width + (bleedEnabled ? BLEED_SIZE_MM * 2 : 0)
    const tocTotalHmm = paper.height + (bleedEnabled ? BLEED_SIZE_MM * 2 : 0)

    // 더미 학년/단원 데이터
    const DUMMY_GRADES = ['초 5-1', '중 1-1', '고 2-1', '초 4-2', '중 2-2', '초 6-1', '고 3-2', '중 1-2']
    const DUMMY_UNITS = ['4단원. 약분과 통분', '1단원. 소인수분해', '1단원. 지수함수와 로그함수', '2단원. 약수와 배수', '3단원. 소수의 나눗셈', '2단원. 삼각형', '1단원. 공간도형과 공간좌표', '2단원. 도형의 닮음과 피타고라스 정리']

    const tocModules: TocModuleData[] = rawModules.map((mod, mi) => ({
      chapterNumber: mod.chapterNumber ?? (mi + 5),
      moduleName: mod.moduleName,
      colorIndex: Math.min(mi, 2),
      lessons: mod.lessons.map((l, li): TocLesson => ({
        lessonName: l.lessonName,
        startPage: l.startPage,
        grade: l.grade || DUMMY_GRADES[(mi * 3 + li) % DUMMY_GRADES.length],
        unitInfo: l.unitInfo || DUMMY_UNITS[(mi * 3 + li) % DUMMY_UNITS.length],
      })),
    }))

    const tocPrintRef = useRef<HTMLDivElement>(null)
    const tocReactToPrintFn = useReactToPrint({
      contentRef: tocPrintRef,
      documentTitle: `${bookLabelToc}_목차`,
      pageStyle: `
        @page { size: ${tocTotalWmm}mm ${tocTotalHmm}mm; margin: 0; }
        @media print {
          html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print-page { page-break-after: always !important; break-after: page !important; }
          .print-page:last-child { page-break-after: auto !important; break-after: auto !important; }
        }
      `,
    })

    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-white sticky top-0 z-10 print:hidden">
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <FileText size={14} />
            <span>목차 · 2페이지 · p.2~3</span>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-slate-600">
              도련 (3mm)
              <button
                type="button"
                role="switch"
                aria-checked={bleedEnabled}
                onClick={() => setBleedEnabled(!bleedEnabled)}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                  bleedEnabled ? 'bg-amber-500' : 'bg-gray-300'
                }`}
              >
                <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  bleedEnabled ? 'translate-x-4' : 'translate-x-0'
                }`} />
              </button>
            </label>
            <button
              onClick={() => navigate(`/contents/books/${bookId}?tab=3`)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              <ArrowLeft className="w-4 h-4" /> 돌아가기
            </button>
            <button
              onClick={() => tocReactToPrintFn()}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-amber-500 text-slate-900 rounded-lg text-sm font-medium hover:bg-amber-400 transition-colors"
            >
              <Printer size={16} /> PDF 다운로드
            </button>
          </div>
        </div>
        <div className="flex overflow-auto flex-1 justify-center py-6">
          <div
            ref={tocPrintRef}
            className="border border-gray-300 shadow-lg print:shadow-none print:border-0"
            style={{ width: tocTotalW }}
          >
            <style>{`.print-page { overflow: hidden; box-sizing: border-box; height: ${tocTotalH}px; }`}</style>
            <TocPage
              modules={tocModules}
              pageWidth={pageWidthPx}
              pageHeight={pageHeightPx}
              bleedPx={tocBleedPx}
            />
          </div>
        </div>
      </div>
    )
  }

  // ─── 레슨 프리뷰 ──────────────────────────────────────────────────

  // URL search params에서 메타 정보 추출
  const lessonName = searchParams.get('name') || '레슨'
  const startPageNumber = Number(searchParams.get('startPage') || '3')
  const bookLabel = searchParams.get('bookLabel') || ''
  const chapterNumber = searchParams.get('chapter') || '01'
  const grade = searchParams.get('grade') || ''
  const unitLabel = searchParams.get('unitLabel') || ''
  const unitName = searchParams.get('unitName') || ''
  const quetureTitle = searchParams.get('quetureTitle') || lessonName

  // Fetch ALL quizzes for this lesson
  const { data: rawQuizzes = [], isLoading, error } = useQuery({
    queryKey: ['lesson-quizzes', lessonId],
    queryFn: () => quizScriptApi.getContentQuizzes(Number(lessonId), undefined, false, true),
    enabled: !!lessonId,
  })

  // Step별 분배
  const { steps, flat: allQuizzes } = useMemo(() => distributeQuizzes(rawQuizzes), [rawQuizzes])

  // Paper dimensions
  const paper = PAPER_SIZES.A4
  const pageWidthPx = mmToPx(paper.width)
  const pageHeightPx = mmToPx(paper.height)
  const bleedPx = bleedEnabled ? mmToPx(BLEED_SIZE_MM) : 0
  const totalPageWidthPx = pageWidthPx + bleedPx * 2
  const totalPageHeightPx = pageHeightPx + bleedPx * 2
  const totalPageWmm = paper.width + (bleedEnabled ? BLEED_SIZE_MM * 2 : 0)
  const totalPageHmm = paper.height + (bleedEnabled ? BLEED_SIZE_MM * 2 : 0)
  const gutterPx = 0

  // Measurement
  const { measuredHeights, isMeasuring } = useQuizMeasurement(
    measureRef,
    allQuizzes,
    ['A4', 'portrait', 1, pageWidthPx, true, bleedEnabled]
  )
  const paddedHeights = useMemo(() => padMeasuredHeights(measuredHeights), [measuredHeights])
  const stepPages = usePageSplitter(steps, paddedHeights, pageHeightPx, QUIZZES_PER_STEP)
  const { footerTop } = getPageLayout(pageHeightPx)

  // Step 4 inline check
  const step4FitsInline = useMemo(() => {
    if (stepPages.length === 0) return false
    const lastPage = stepPages[stepPages.length - 1]
    let usedHeight = 0
    if (lastPage.isFirstOfStep) {
      if (lastPage.stepIdx === 0) {
        usedHeight += LAYOUT.header.totalHeight + LAYOUT.step1FirstPaddingTop + 51 + LAYOUT.quizTopPadding.afterStepTitle
      } else {
        usedHeight += (LAYOUT.step2.barHeight + LAYOUT.step2.curveHeight) + LAYOUT.quizTopPadding.step23FirstPage
      }
    } else {
      if (lastPage.stepIdx === 0) {
        usedHeight += LAYOUT.header.barHeight + LAYOUT.quizTopPadding.continuationGap
      } else {
        usedHeight += LAYOUT.step2.barHeight + LAYOUT.quizTopPadding.continuationGap
      }
    }
    const globalOffset = lastPage.stepIdx * QUIZZES_PER_STEP
    lastPage.quizzes.forEach((_: QuizScript, i: number) => {
      const quizIdx = globalOffset + (lastPage.quizStartNum - 1) + i
      usedHeight += paddedHeights[quizIdx] || 400
    })
    const footerSpace = 72 + LAYOUT.footer.bottomGap
    const availableSpace = pageHeightPx - usedHeight - footerSpace
    return availableSpace >= STEP4_LAYOUT.inlineHeight
  }, [stepPages, paddedHeights, pageHeightPx])

  // 더미 페이지 필요 여부 (마지막 페이지가 홀수면 더미 삽입 → 다음 레슨이 홀수에서 시작)
  const contentPageCount = useMemo(() => {
    let count = stepPages.length
    if (STEP4_QUESTIONS.length > 0 && !step4FitsInline && stepPages.length > 0) count++
    return count
  }, [stepPages, step4FitsInline])

  const lastContentPageNum = startPageNumber + contentPageCount - 1
  const needsMemoPage = lastContentPageNum % 2 === 0 // 짝수에서 끝나면 더미 필요 (다음 레슨이 짝수에서 시작하도록)

  const lessonPageCount = contentPageCount + (needsMemoPage ? 1 : 0)

  // 측정 완료 시 자동 저장 (측정값 안정화 대기)
  const queryClient = useQueryClient()
  const [saved, setSaved] = useState(false)
  const savedPageCountRef = useRef<number>(0)
  const savePagesMutation = useMutation({
    mutationFn: (pageCount: number) =>
      printBookApi.updatePages(Number(bookId), {
        totalPageCount: 0,
        lessons: [{ lessonId: Number(lessonId), pageCount, startPage: startPageNumber, hasMemoPage: false }],
      }),
    onSuccess: () => {
      setSaved(true)
      queryClient.invalidateQueries({ queryKey: ['print-book', bookId] })
    },
  })

  // 측정값이 실제 DOM 기반인지 확인 (fallback 400px가 아닌 실측값)
  const hasMeasuredHeights = !isMeasuring && measuredHeights.length > 0 && measuredHeights.some(h => h !== 400 && h > 0)

  useEffect(() => {
    if (hasMeasuredHeights && lessonPageCount > 0 && bookId && bookId !== 'new' && !savePagesMutation.isPending) {
      // 이전 저장값과 다를 때만 저장 (안정화 확인)
      if (savedPageCountRef.current !== lessonPageCount) {
        savedPageCountRef.current = lessonPageCount
        setSaved(false)
        // 측정값 안정화를 위해 약간의 딜레이
        const timer = setTimeout(() => {
          savePagesMutation.mutate(lessonPageCount)
        }, 500)
        return () => clearTimeout(timer)
      }
    }
  }, [hasMeasuredHeights, lessonPageCount, bookId])

  const reactToPrintFn = useReactToPrint({
    contentRef: printRef,
    documentTitle: `${bookLabel}_${lessonName}`,
    pageStyle: `
      @page { size: ${totalPageWmm}mm ${totalPageHmm}mm; margin: 0; }
      @media print {
        html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .print-page { page-break-after: always !important; break-after: page !important; position: relative !important; }
        .print-page:last-child { page-break-after: auto !important; break-after: auto !important; }
      }
    `,
  })

  return (
    <div className="flex flex-col h-full">
      {/* Top: Controls */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-white sticky top-0 z-10 print:hidden">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <FileText size={14} />
            <span>
              {lessonName} · 시작 p.{startPageNumber} · {lessonPageCount}페이지
              · 퀴즈 {rawQuizzes.length}개 (분배 {STEP_QUIZ_COUNTS.join('+')}={TOTAL_QUIZ_NEEDED})
            </span>
          </div>
          {lessonPageCount > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-400">|</span>
              <span className="text-slate-500">{lessonPageCount}p</span>
              {saved && (
                <span className="flex items-center gap-1 text-xs text-green-600">
                  <CheckCircle2 className="w-3 h-3" /> 저장됨
                </span>
              )}
              {savePagesMutation.isPending && (
                <span className="flex items-center gap-1 text-xs text-slate-400">
                  <Loader2 className="w-3 h-3 animate-spin" /> 저장 중
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-600">
            도련 (3mm)
            <button
              type="button"
              role="switch"
              aria-checked={bleedEnabled}
              onClick={() => setBleedEnabled(!bleedEnabled)}
              className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                bleedEnabled ? 'bg-amber-500' : 'bg-gray-300'
              }`}
            >
              <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                bleedEnabled ? 'translate-x-4' : 'translate-x-0'
              }`} />
            </button>
          </label>
          <button
            onClick={() => navigate(`/contents/books/${bookId}?tab=3`)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
          >
            <ArrowLeft className="w-4 h-4" /> 돌아가기
          </button>
          <button
            onClick={() => reactToPrintFn()}
            disabled={allQuizzes.length === 0 || isMeasuring}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-amber-500 text-slate-900 rounded-lg text-sm font-medium hover:bg-amber-400 disabled:opacity-50 transition-colors"
          >
            {isMeasuring ? (
              <><Loader2 size={16} className="animate-spin" /> 측정 중...</>
            ) : (
              <><Printer size={16} /> PDF 다운로드</>
            )}
          </button>
        </div>
      </div>

      {/* A4 Preview */}
      <div className="flex overflow-auto flex-1 justify-center py-6">
        <div className="flex flex-col gap-4 items-center">

          {/* Loading */}
          {isLoading && (
            <div className="flex gap-2 items-center py-12 text-slate-500">
              <Loader2 size={20} className="animate-spin" />
              <span className="text-sm">퀴즈 데이터 로딩 중...</span>
            </div>
          )}

          {/* Error */}
          {error && !isLoading && (
            <div className="flex items-center gap-2 p-4 text-red-700 bg-red-50 rounded-lg border border-red-200">
              <AlertCircle className="w-5 h-5" />
              퀴즈 데이터를 불러오는데 실패했습니다.
            </div>
          )}

          {/* Hidden measurement container */}
          {!isLoading && allQuizzes.length > 0 && (
            <div
              ref={measureRef}
              aria-hidden
              style={{
                position: 'absolute',
                left: -9999,
                top: 0,
                width: `${pageWidthPx - LAYOUT.pageSidePadding * 2 - gutterPx}px`,
                visibility: 'hidden',
                lineHeight: 1.6,
              }}
            >
              {allQuizzes.map((script, i) => (
                <div key={`measure-${i}`} data-quiz-measure style={{ paddingBottom: `${LAYOUT.questionSpacing}px` }}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <QuizRenderer
                      quizType={toQuizData(script).type}
                      quizId={String(script.quizId ?? '')}
                      quiz={toQuizData(script)}
                      mode="print"
                      number={i + 1}
                      hideSubmitButton
                      disableSound
                      compact
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* A4 Paper Preview */}
          {!isLoading && allQuizzes.length > 0 && (
            <div
              ref={printRef}
              className="bg-white border border-gray-300 shadow-lg print:shadow-none print:border-0"
              style={{ width: `${totalPageWidthPx}px`, lineHeight: 1.6 }}
            >
              <style>{`
                .print-page { overflow: hidden; box-sizing: border-box; height: ${totalPageHeightPx}px; }
              `}</style>

              {stepPages.map((sp, pageIdx) => {
                const pageNumber = startPageNumber + pageIdx
                const isEven = pageNumber % 2 === 0
                const isStep1 = sp.stepIdx === 0
                const isStep1FirstPage = isStep1 && sp.isFirstOfStep

                return (
                  <div key={`page-${pageIdx}`} className="print-page relative" style={{ width: `${totalPageWidthPx}px` }}>
                    <div className="relative" style={{
                      marginLeft: isEven && gutterPx > 0 ? gutterPx : 0,
                      marginRight: !isEven && gutterPx > 0 ? gutterPx : 0,
                      height: totalPageHeightPx,
                    }}>
                      {/* Header */}
                      {isStep1FirstPage ? (
                        <>
                          <PageHeader isEven={isEven} bleedPx={bleedPx} />
                          <ChapterNumber number={chapterNumber} bleedPx={bleedPx} />
                          <TitleQueture grade={grade} unitLabel={unitLabel} unitName={unitName} quetureTitle={quetureTitle} bleedPx={bleedPx} />
                        </>
                      ) : isStep1 ? (
                        <PageHeader isEven={isEven} bleedPx={bleedPx} />
                      ) : (
                        <div className="relative">
                          <StepHeader stepIndex={sp.stepIdx} isEven={isEven} bleedPx={bleedPx} />
                          {sp.isFirstOfStep && <TitleStepSvg stepIndex={sp.stepIdx} isAbsolute />}
                        </div>
                      )}

                      {/* Content */}
                      <div style={{
                        paddingTop: isStep1FirstPage ? LAYOUT.step1FirstPaddingTop + bleedPx : 0,
                        position: 'relative',
                        zIndex: 1,
                      }}>
                        {isStep1FirstPage && <TitleStepSvg stepIndex={0} />}
                        {(() => {
                          let quizPadTop: number
                          let quizMarginTop = 0
                          if (isStep1FirstPage) {
                            quizPadTop = LAYOUT.quizTopPadding.afterStepTitle
                          } else if (sp.isFirstOfStep) {
                            quizPadTop = LAYOUT.quizTopPadding.step23FirstPage
                          } else if (isStep1) {
                            quizPadTop = 0
                            quizMarginTop = -(LAYOUT.header.curveHeight - LAYOUT.quizTopPadding.continuationGap)
                          } else {
                            quizPadTop = LAYOUT.quizTopPadding.continuationGap - LAYOUT.step2.curveHeight
                          }
                          return (
                            <div style={{
                              paddingTop: quizPadTop,
                              marginTop: quizMarginTop,
                              paddingLeft: LAYOUT.pageSidePadding + bleedPx,
                              paddingRight: LAYOUT.pageSidePadding + bleedPx,
                            }}>
                              {sp.quizzes.map((script: QuizScript, i: number) => (
                                <PrintQuizItem
                                  key={`${sp.stepIdx}-${sp.quizStartNum + i}`}
                                  script={script}
                                  number={sp.quizStartNum + i}
                                  stepIdx={sp.stepIdx}
                                  compact
                                />
                              ))}
                            </div>
                          )
                        })()}
                      </div>

                      {/* Step 4 inline */}
                      {pageIdx === stepPages.length - 1 && step4FitsInline && STEP4_QUESTIONS.length > 0 && (
                        <div style={{ position: 'absolute', bottom: totalPageHeightPx - (footerTop + bleedPx) + 20, left: bleedPx, right: bleedPx }}>
                          <Step4Section questions={STEP4_QUESTIONS} />
                        </div>
                      )}

                      {/* Footer */}
                      {<PageFooter pageNumber={pageNumber} isEven={isEven} bookLabel={bookLabel} footerTop={footerTop} bleedPx={bleedPx} />}
                    </div>
                  </div>
                )
              })}

              {/* Step 4 separate page */}
              {!step4FitsInline && STEP4_QUESTIONS.length > 0 && stepPages.length > 0 && (() => {
                const step4PageNumber = startPageNumber + stepPages.length
                const step4IsEven = step4PageNumber % 2 === 0
                return (
                  <div className="print-page relative" style={{ width: `${totalPageWidthPx}px` }}>
                    <div className="relative" style={{
                      marginLeft: step4IsEven && gutterPx > 0 ? gutterPx : 0,
                      marginRight: !step4IsEven && gutterPx > 0 ? gutterPx : 0,
                      height: totalPageHeightPx,
                    }}>
                      <div className="relative">
                        <StepHeader stepIndex={3} isEven={step4IsEven} bleedPx={bleedPx} />
                        <TitleStepSvg stepIndex={3} isAbsolute />
                      </div>
                      <div style={{ paddingTop: LAYOUT.quizTopPadding.step23FirstPage, paddingLeft: bleedPx, paddingRight: bleedPx }}>
                        <Step4Section questions={STEP4_QUESTIONS} showTitle={false} />
                      </div>
                      <PageFooter pageNumber={step4PageNumber} isEven={step4IsEven} bookLabel={bookLabel} footerTop={footerTop} bleedPx={bleedPx} />
                    </div>
                  </div>
                )
              })()}
              {/* Memo page */}
              {needsMemoPage && (
                <MemoPage
                  pageNumber={lastContentPageNum + 1}
                  pageWidth={totalPageWidthPx}
                  pageHeight={totalPageHeightPx}
                />
              )}
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !error && allQuizzes.length === 0 && (
            <div className="py-12 text-sm text-center text-slate-400">이 레슨에 퀴즈가 없습니다.</div>
          )}
        </div>
      </div>
    </div>
  )
}
